import { useI18n } from '@affine/i18n';
import { CloseIcon, SearchIcon } from '@blocksuite/icons/rc';
import clsx from 'clsx';
import { type ReactNode, useRef } from 'react';

import * as styles from './index.css';

export interface SidebarSearchFieldProps {
  value: string;
  onChange: (value: string) => void;
  onClear?: () => void;
  placeholder: string;
  ariaLabel: string;
  clearLabel?: string;
  clearButtonTestId?: string;
  className?: string;
  railAligned?: boolean;
  disabled?: boolean;
  action?: ReactNode;
}

/** Controlled sidebar search aligned to the canonical navigation row gutter. */
export function SidebarSearchField({
  value,
  onChange,
  onClear,
  placeholder,
  ariaLabel,
  clearLabel,
  clearButtonTestId = 'sidebar-search-clear',
  className,
  railAligned = false,
  disabled = false,
  action,
}: SidebarSearchFieldProps) {
  const t = useI18n();
  const input = useRef<HTMLInputElement>(null);
  return (
    <div
      className={clsx(
        styles.root,
        railAligned && styles.railAligned,
        className
      )}
    >
      <SearchIcon aria-hidden />
      <input
        ref={input}
        type="text"
        disabled={disabled}
        role="searchbox"
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={value}
        onChange={event => onChange(event.target.value)}
      />
      {value ? (
        <button
          type="button"
          disabled={disabled}
          className={styles.clear}
          aria-label={clearLabel ?? t['com.streamlain.appBases.search.clear']()}
          onClick={() => {
            if (onClear) onClear();
            else onChange('');
            input.current?.focus();
          }}
          data-testid={clearButtonTestId}
        >
          <CloseIcon aria-hidden />
        </button>
      ) : null}
      {action ? <div className={styles.action}>{action}</div> : null}
    </div>
  );
}
